import { __decorate } from "tslib";
import { consumeContext, watchContext } from '../../base/context';
import { mediaContext, MediaRemoteControl } from '../../media';
import { setAttribute } from '../../utils/dom';
import { ToggleButtonElement } from '../toggle-button';
export const PLAY_BUTTON_ELEMENT_TAG_NAME = 'vds-play-button';
/**
 * A button for toggling the playback state (play/pause) of the current media.
 *
 * 💡 The following attributes are updated for your styling needs:
 *
 * - `media-can-play`: Applied when media can begin playback.
 * - `media-paused`: Applied when media playback has paused.
 * - `media-waiting`: Applied when playback has stopped because of a lack of temporary data.
 * - `media-ended`: Applied when playback has reached the end.
 *
 * @tagname vds-play-button
 * @slot - Used to pass content into the play toggle for showing play/pause states.
 * @csspart button - The button element (`<button>`).
 * @example
 * ```html
 * <vds-play-button>
 *   <div class="play">Play</div>
 *   <div class="pause">Pause</div>
 * </vds-play-button>
 * ```
 * @example
 * ```css
 * vds-play-button:not([media-paused]) .play {
 *   display: none;
 * }
 *
 * vds-play-button[media-paused] .pause {
 *   display: none;
 * }
 * ```
 */
export class PlayButtonElement extends ToggleButtonElement {
    constructor() {
        super(...arguments);
        this._mediaRemote = new MediaRemoteControl(this);
        this.label = 'Play';
        // Transforming `paused` to `!paused` to indicate whether playback has initiated/resumed. Can't
        // use `playing` because there could be a buffering delay (we want immediate feedback).
        this.pressed = false;
    }
    _handleButtonClick(event) {
        if (this.pressed) {
            this._mediaRemote.pause(event);
        }
        else {
            this._mediaRemote.play(event);
        }
    }
    _handleCanPlayContextUpdate(canPlay) {
        setAttribute(this, 'media-can-play', canPlay);
    }
    _handleWaitingContextUpdate(waiting) {
        setAttribute(this, 'media-waiting', waiting);
    }
    _handlePausedContextUpdate(paused) {
        setAttribute(this, 'media-paused', paused);
    }
    _handleEndedContextUpdate(ended) {
        setAttribute(this, 'media-ended', ended);
    }
}
__decorate([
    consumeContext(mediaContext.paused, { transform: (p) => !p })
], PlayButtonElement.prototype, "pressed", void 0);
__decorate([
    watchContext(mediaContext.canPlay)
], PlayButtonElement.prototype, "_handleCanPlayContextUpdate", null);
__decorate([
    watchContext(mediaContext.waiting)
], PlayButtonElement.prototype, "_handleWaitingContextUpdate", null);
__decorate([
    watchContext(mediaContext.paused)
], PlayButtonElement.prototype, "_handlePausedContextUpdate", null);
__decorate([
    watchContext(mediaContext.ended)
], PlayButtonElement.prototype, "_handleEndedContextUpdate", null);
